/**
 * Sign in and register.
 *
 * Note what is *not* here: no token is written to localStorage. The refresh token
 * arrives as an httpOnly cookie the browser stores and this code never sees, and the
 * access token stays in a module variable in `api/client`.
 */
import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../store/session';
import { ApiRequestError } from '../api/client';
import { useDeviceProfile } from '../device/useDeviceProfile';

export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const { login, register } = useSession();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Where to go once signed in.
   *
   * Normally the profile picker. The exception is a pairing code, and it is not a nicety:
   * the logged-out phone is the *ordinary* case for a scanned QR — a browser tab that has
   * not been opened in a week has no session — and this screen is what a scan lands on.
   * Sending that person to the picker discards the code they just scanned, so the QR path
   * ended at a profile list with no explanation, every time.
   *
   * The URL still carries it: logged out, `/pair?code=…` matches the catch-all route, so
   * nothing navigated and the query string is intact.
   *
   * Only this one destination is carried across, rather than a general "return to where
   * you were". Pairing is the case where the URL holds something that cannot be
   * reconstructed by navigating back to it, which is what makes it worth the special case.
   */
  function destination(): string {
    if (location.pathname !== '/pair') return '/profiles';
    const code = new URLSearchParams(location.search).get('code');
    return code ? `/pair?code=${encodeURIComponent(code)}` : '/profiles';
  }

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isRegister = mode === 'register';
  const isTv = useDeviceProfile() === 'tv';

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await (isRegister ? register(email, password) : login(email, password));
      await navigate(destination(), { replace: true });
    } catch (err) {
      if (err instanceof ApiRequestError && err.code === 'REGISTRATION_DISABLED') {
        setError('Bu sunucuda kayıt kapalı.');
      } else {
        setError(err instanceof Error ? err.message : 'Giriş başarısız');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <h1>Filmodasi</h1>
      <form onSubmit={submit}>
        <label>
          E-posta
          <input
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label>
          Parola
          <input
            type="password"
            autoComplete={isRegister ? 'new-password' : 'current-password'}
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <button type="submit" disabled={busy}>
          {busy ? 'Bekleyin…' : isRegister ? 'Hesap oluştur' : 'Giriş yap'}
        </button>
      </form>

      {/*
        On a television this is the way in, and the form above is the fallback — which is
        why it sits directly under the button rather than beneath the register link. It is
        shown only on the TV: on a phone the password form is already the easy path, and
        pairing needs a signed-in phone to approve it anyway.
      */}
      {isTv && !isRegister && (
        <p className="auth__pair">
          Uzaktan kumandayla yazmak istemiyor musunuz?{' '}
          <Link to="/pair">Telefonla giriş yapın</Link>
        </p>
      )}

      <p className="auth__switch">
        {isRegister ? (
          <>
            Zaten hesabınız var mı? <Link to="/login">Giriş yapın</Link>
          </>
        ) : (
          <>
            Hesabınız yok mu? <Link to="/register">Kayıt olun</Link>
          </>
        )}
      </p>
    </main>
  );
}
